import { FontAwesomeIconProps } from '@fortawesome/react-fontawesome';
import classNames from 'classnames';
import * as React from 'react';
import { ViewportList } from 'react-viewport-list';
import { v4 as uuid } from 'uuid';

import { faChevronDown, faChevronUp } from '@fortawesome/pro-regular-svg-icons';
import { SelectOption } from '../../../@types/shared';
import Checkbox from '../../atoms/Checkbox';
import DropdownItem from '../../atoms/DropdownItem';
import Overlay from '../../atoms/Overlay';
import Radio from '../../atoms/Radio';
import Spinner from '../../atoms/Spinner';
import Text from '../../atoms/Text';
import SearchBar from '../../molecules/SearchBar';

import { Icon } from '../../atoms';
import styles from './MultipleSelectFilter.module.scss';

interface CheckboxProps {
  type: 'checkbox';
  value: Array<string>;
}

interface RadioSelectProps {
  type: 'radio' | 'select';
  value: string;
}

export type PropsCheckBox = Omit<
  React.HTMLAttributes<HTMLElement>,
  'onChange'
> & {
  name: string;
  icon?: FontAwesomeIconProps['icon'];
  options: Array<SelectOption>;
  label?: string;
  helperText?: string;
  errorText?: string;
  loading?: boolean;
  search?: string;
  onOpen?: () => void;
  onChange?: (name: string, newValue: string | Array<string>) => void;
  onSearch?: (search: string) => void;
  disabled?: boolean;
  withSearch?: boolean;
  searchPlaceholder?: string;
} & CheckboxProps;

export type PropsSelect = Omit<
  React.HTMLAttributes<HTMLElement>,
  'onChange'
> & {
  name: string;
  icon?: FontAwesomeIconProps['icon'];
  options: Array<SelectOption>;
  label?: string;
  helperText?: string;
  errorText?: string;
  loading?: boolean;
  search?: string;
  onOpen?: () => void;
  onChange?: (name: string, newValue: string | Array<string>) => void;
  onSearch?: (search: string) => void;
  disabled?: boolean;
  withSearch?: boolean;
  searchPlaceholder?: string;
} & RadioSelectProps;

export type Props = PropsCheckBox | PropsSelect;

const parseValue = (value: Props['value']) => {
  if (Array.isArray(value) && value.length === 0) {
    return [''];
  }
  return value;
};

/**
 *
 * MultipleSelectFilter - A combobox component for dashboard pages.
 *
 */
export const MultipleSelectFilter: React.FunctionComponent<Props> = ({
  id,
  className,
  style,
  name,
  type,
  label,
  icon,
  loading,
  value,
  search: controlledSearch,
  searchPlaceholder = 'Pesquisar',
  options,
  helperText,
  errorText,
  onOpen,
  onChange,
  onSearch,
  disabled = false,
  withSearch = false,

  ...inputProps
}) => {
  const [show, setShow] = React.useState(false);
  const [target, setTarget] = React.useState<HTMLDivElement | null>(null);
  const [selected, setSelected] = React.useState(parseValue(value));
  const viewportRef = React.useRef<HTMLDivElement | null>(null);

  const [search, setSearch] = React.useState('');
  const effectiveSearch = controlledSearch ?? search;
  const searchBarRef = React.useRef<HTMLInputElement | null>(null);

  const [focusedOptionIndex, setFocusedOptionIndex] = React.useState(-1);
  const [overlayPlacement, setOverlayPlacement] = React.useState<
    string | undefined
  >('bottom-start');

  // Update selections when value changes on parent
  React.useEffect(() => {
    setSelected(parseValue(value));
  }, [value]);

  React.useEffect(() => {
    if (searchBarRef.current && show) {
      searchBarRef.current.focus();
    }
  });

  const filteredOptions = React.useMemo(
    () =>
      options.filter(
        (option) =>
          option.label.toLowerCase().includes(effectiveSearch.toLowerCase()) ||
          option.id === '',
      ),
    [options, effectiveSearch],
  );

  const inputId = id ?? uuid();

  // Get value to display
  const displayValue = React.useMemo(() => {
    const parsedValue = parseValue(value);
    if (Array.isArray(parsedValue)) {
      return options
        .filter((opt) => parsedValue.includes(opt.id))
        .map((opt) => opt.label);
    }
    return [options.find((opt) => opt.id === parsedValue)?.label];
  }, [value, options]);

  /**
   * Handle change to selection when popover is open
   */
  const handleChange = React.useCallback(
    (event: React.ChangeEvent<HTMLInputElement>) => {
      if (event.target.type === 'radio') {
        setSelected(event.target.value);
      } else if (event.target.type === 'checkbox') {
        const checkSelections = selected as string[];
        // Reset selection if:
        //  - default (key: '') is selected
        //  - no options is selected
        if (
          event.target.value === '' ||
          (!event.target.checked && checkSelections.length === 1)
        ) {
          setSelected(['']);
          return;
        }
        setSelected(
          event.target.checked
            ? [...checkSelections.filter((e) => e !== ''), event.target.value]
            : checkSelections.filter((e) => e !== event.target.value),
        );
      }
    },
    [selected],
  );

  /**
   * Handle change to selection when popover is open
   */
  const handleSelect = React.useCallback(
    (v: string) => {
      setSelected(v);
      setShow(false);
      if (onChange && type === 'select') {
        onChange(name, v);
      }
    },
    [onChange, name, type],
  );

  /**
   * Notify changes when overlay is closed
   */
  const handleOverlayHide = React.useCallback(() => {
    if (onChange && type === 'checkbox') {
      onChange(name, selected as string[]);
    } else if (onChange && type !== 'checkbox') {
      onChange(name, selected as string);
    }
  }, [name, type, selected, onChange]);

  const handleOnBlur: React.FocusEventHandler<HTMLInputElement> =
    React.useCallback(
      (evt) => {
        const modifiedEvent = { ...evt };
        modifiedEvent.target.value = modifiedEvent.target.value?.trim();
        inputProps.onBlur?.(modifiedEvent);
      },
      [inputProps],
    );

  const handleOpen = React.useCallback(() => {
    if (disabled) return;
    // If shown, hide and save filters
    if (show) handleOverlayHide();

    // Toggle show
    setShow((s) => !s);
    if (!show && onOpen) onOpen();
  }, [disabled, handleOverlayHide, onOpen, show]);

  const handleKeyDown = React.useCallback(
    (event: React.KeyboardEvent<HTMLDivElement>) => {
      if (disabled) return;

      const { key } = event;
      const totalButtons = filteredOptions.length;

      setShow(true);
      const listBox = document.querySelector('[role="listbox"]');
      if (!listBox) {
        return;
      }

      let index = 0;
      let option;

      switch (key) {
        case 'ArrowUp':
          event.preventDefault();
          index = (focusedOptionIndex + totalButtons - 1) % totalButtons;
          option = listBox.querySelector(`[data-option-index="${index}"]`);

          option?.scrollIntoView({
            block: 'nearest',
          });
          setFocusedOptionIndex(index);
          break;
        case 'ArrowDown':
          event.preventDefault();
          index = (focusedOptionIndex + 1) % totalButtons;
          option = listBox.querySelector(`[data-option-index="${index}"]`);
          option?.scrollIntoView({
            block: 'nearest',
          });
          setFocusedOptionIndex(index);
          break;
        case 'Enter':
        case ' ':
          event.preventDefault();
          if (type !== 'checkbox') {
            handleSelect(filteredOptions[focusedOptionIndex].id);
          } else {
            const selectedArray = selected as string[];
            /** Add or remove the new selected option */
            const newData = selectedArray.find(
              (s) => s === filteredOptions[focusedOptionIndex].id,
            )
              ? selectedArray.filter(
                  (s) => s !== filteredOptions[focusedOptionIndex].id,
                )
              : [
                  ...(selectedArray as string[]),
                  filteredOptions[focusedOptionIndex].id,
                ];
            setSelected(newData);
            onChange?.(name, newData as string & string[]);
          }
          break;
        case 'Escape':
          event.stopPropagation();
          setShow(false);
          break;
        default:
          break;
      }
    },
    [
      disabled,
      filteredOptions,
      focusedOptionIndex,
      handleSelect,
      name,
      onChange,
      selected,
      type,
    ],
  );

  // Clear search input when opening overlay to avoid sudden UI changes on close
  React.useEffect(() => {
    if (show && controlledSearch === undefined) {
      setSearch('');
    }
  }, [show, controlledSearch]);

  return (
    <div
      className={classNames(styles.wrapper, className, {
        disabled,
        error: !!errorText,
      })}
      style={style}
    >
      <div className="d-flex flex-items-center gap-2 mb-2">
        {icon && <Icon icon={icon} />}
        {label && (
          <Text as="label" size="sm2" weight="bold" htmlFor={inputId}>
            {label}
          </Text>
        )}
      </div>

      <div className="d-flex flex-justify-between align-items-center gap-2">
        <div className="position-relative flex-1 overflow-hidden">
          <div
            id={inputId}
            className={classNames(styles.input, {
              disabled,
              error: !!errorText,
              [`overlay-${overlayPlacement}`]: !!overlayPlacement && show,
            })}
            ref={setTarget}
            onClick={handleOpen}
            onBlur={handleOnBlur}
            onKeyDown={handleKeyDown}
            role="combobox"
            aria-label={label}
            aria-controls="overlay"
            aria-expanded={show}
            aria-haspopup="listbox"
            tabIndex={0}
          >
            <div className={styles['value-container']}>
              {displayValue[0] && (
                <span
                  key={displayValue[0]}
                  className="bg-blue600_25 py-1 px-2 rounded-md ellipsize"
                >
                  {displayValue[0]}
                </span>
              )}
              {displayValue.length > 1 && (
                <span className="bg-blue600_25 py-1 px-2 rounded-md">
                  {`+${displayValue.length - 1}`}
                </span>
              )}
            </div>
            <Icon
              className={classNames(styles.icon, {
                disabled,
              })}
              size="sm"
              icon={show ? faChevronUp : faChevronDown}
            />
          </div>
        </div>
      </div>

      {helperText && (
        <Text as="span" className="mt-2" size="sm1">
          {helperText}
        </Text>
      )}
      {errorText && (
        <Text as="span" className="mt-2" size="sm1" color="red400">
          {errorText}
        </Text>
      )}

      <Overlay
        id="overlay"
        className={classNames(styles.overlay, 'tooltip')}
        target={target}
        show={show}
        onHide={() => {
          handleOverlayHide();
          setShow(false);
        }}
        placement="bottom-start"
        matchReferenceSize
        offset={0}
        onUpdatePlacement={setOverlayPlacement}
      >
        <div
          className={classNames(styles['overlay-container'], {
            error: !!errorText,
          })}
          ref={viewportRef}
        >
          <div
            className={classNames('px-3 pb-3', {
              'pt-0': withSearch,
              'pt-3': !withSearch,
            })}
          >
            {withSearch && (
              <SearchBar
                className={styles.searchbar}
                placeholder={searchPlaceholder}
                value={effectiveSearch}
                size="sm"
                onChange={(e) => {
                  if (controlledSearch === undefined) {
                    setSearch(e.target.value);
                  }
                  onSearch?.(e.target.value);
                }}
                showIcon={false}
                ref={searchBarRef}
              />
            )}

            {/* Render a radio option menu */}
            {loading && <Spinner size="sm" />}

            {!loading && withSearch && filteredOptions.length === 0 && (
              <Text size="sm2" color="blackLight">
                Nenhum dado encontrado
              </Text>
            )}

            {!loading && (
              <>
                {type === 'radio' && (
                  <ViewportList
                    viewportRef={viewportRef}
                    items={filteredOptions}
                  >
                    {(opt, idx, arr) => (
                      <Radio
                        key={opt.key ?? opt.id}
                        className={classNames(styles.option, {
                          last: idx === arr.length - 1,
                        })}
                        name={name}
                        label={opt.label}
                        value={opt.id}
                        checked={selected === opt.id}
                        onChange={handleChange}
                        ellipsize={false}
                        disabled={opt.disabled}
                      />
                    )}
                  </ViewportList>
                )}

                {/* Render a checkbox option menu */}
                {type === 'checkbox' && (
                  <ViewportList
                    viewportRef={viewportRef}
                    items={filteredOptions}
                  >
                    {(opt, idx, arr) => (
                      <Checkbox
                        key={opt.key ?? opt.id}
                        className={classNames(styles.option, {
                          last: idx === arr.length - 1,
                        })}
                        name={`${name}-${idx}`}
                        label={opt.label}
                        value={opt.id}
                        checked={selected.includes(opt.id)}
                        onChange={handleChange}
                        ellipsize={false}
                        disabled={opt.disabled}
                      />
                    )}
                  </ViewportList>
                )}

                {/* Render a checkbox option menu */}
                {type === 'select' && (
                  <ViewportList
                    viewportRef={viewportRef}
                    items={filteredOptions}
                  >
                    {(opt) => (
                      <DropdownItem
                        key={opt.key ?? opt.id}
                        className={styles.option}
                        onClick={() => !opt.disabled && handleSelect(opt.id)}
                        ellipsize={false}
                        disabled={opt.disabled}
                        role="option"
                      >
                        {opt.label}
                      </DropdownItem>
                    )}
                  </ViewportList>
                )}
              </>
            )}
          </div>
        </div>
      </Overlay>
    </div>
  );
};

export default MultipleSelectFilter;
